import DialogTitle from '@material-ui/core/DialogTitle'
import { Divider, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@material-ui/core'
import { ReportProblemOutlined as WarningIcon } from '@material-ui/icons'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import React from 'react'
import { Form, Formik } from 'formik'
import { makeStyles } from '@material-ui/core/styles'
import DraggablePaper from 'civic-champs-shared/core/confirm-dialog/DraggablePaper'

const useStyles = makeStyles({
  title: { cursor: 'move', lineHeight: '36px', padding: '20px 20px 16px 20px' },
  icon: { fontSize: '40px', display: 'block', marginRight: '12px', color: '#F4BF2C' },
  caption: { flexWrap: 'nowrap' },
  text: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#2D3038',
    padding: '8px 0',
    textAlign: 'justify',
  },
  content: {
    padding: '0 20px',
  },
  boldText: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#2D3038',
  },
  button: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    borderRadius: '100px',
    color: '#191C1D',
    padding: '8px 24px',
    backgroundColor: '#F4BF2C',
    textTransform: 'none',
    lineHeight: '20px',
    letterSpacing: '0.1px',
  },
  cancelButton: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    color: '#74777F',
    borderRadius: '100px',
    padding: '8px 24px',
    textTransform: 'none',
    lineHeight: '20px',
    letterSpacing: '0.1px',
  },
  actions: {
    padding: '8px 20px 20px',
  },
  divider: {
    margin: '8px 0 12px',
  },
  versionLine: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#2D3038',
    marginTop: 4,
  },
  question: {
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#2D3038',
    marginTop: 16,
    marginBottom: 4,
  },
  radioGroup: {
    marginTop: 4,
  },
  radioRoot: {
    alignItems: 'flex-start',
    marginLeft: 0,
    marginRight: 0,
  },
  radioControl: {
    padding: '5px 9px',
  },
  radioChecked: {
    color: '#0F5DB5 !important',
  },
  optionLabel: {
    fontFamily: 'Nunito, sans-serif',
    fontSize: '14px',
    lineHeight: '20px',
    color: '#2D3038',
    paddingTop: 6,
  },
  optionChoice: {
    fontWeight: 700,
  },
  optionDescription: {
    fontWeight: 400,
    color: '#747778',
  },
})

export interface ConfirmQuestionnaireVersionDialogProps {
  showing: boolean
  close: () => void
  complete: (value: boolean | { updateMinimumRequiredVersion: boolean }) => void
  newVersion: number
  minimumRequiredVersion: number
  showMinimumRequiredChoice?: boolean
}

export const ConfirmQuestionnaireVersionDialog = ({
  showing,
  close,
  complete,
  newVersion,
  minimumRequiredVersion,
  showMinimumRequiredChoice = false,
}: ConfirmQuestionnaireVersionDialogProps) => {
  const classes = useStyles()

  const handleComplete = (values: { updateMinimumRequiredVersion: boolean }) => {
    if (showMinimumRequiredChoice) {
      complete(values)
      return
    }
    complete(true)
  }

  return (
    // @ts-ignore
    <Dialog
      open={showing}
      onClose={() => close()}
      // @ts-ignore
      PaperComponent={DraggablePaper}
      // @ts-ignore
      PaperProps={{ handle: '#confirm-questionnaire-version-dialog-title' }}
      aria-labelledby="confirm-questionnaire-version-dialog-title"
      maxWidth="sm"
      fullWidth
    >
      <Formik initialValues={{ updateMinimumRequiredVersion: true }} onSubmit={handleComplete}>
        {({ values, setFieldValue }) => (
          <Form>
            <DialogTitle
              classes={{ root: classes.title }}
              disableTypography={true}
              id="confirm-questionnaire-version-dialog-title"
            >
              <Grid container direction="row" alignItems="center" classes={{ root: classes.caption }}>
                <Grid item>
                  <WarningIcon classes={{ root: classes.icon }} />
                </Grid>
                <Grid item>
                  <Typography classes={{ root: classes.boldText }}>
                    Are you sure you want to make these changes?
                  </Typography>
                </Grid>
              </Grid>
            </DialogTitle>
            <DialogContent classes={{ root: classes.content }}>
              <DialogContentText classes={{ root: classes.text }}>
                There is currently no way to revert to a previous version. If the changes are extensive, creating an
                entirely new survey is recommended.
              </DialogContentText>
              <Divider className={classes.divider} />
              <Typography className={classes.versionLine}>
                You are creating <strong>Version {newVersion}</strong> of this questionnaire.
              </Typography>
              {showMinimumRequiredChoice && (
                <>
                  <Typography className={classes.versionLine}>
                    The minimum required version is <strong>Version {minimumRequiredVersion}</strong>.
                  </Typography>
                  <Typography className={classes.question}>
                    Would you like to update the minimum required version?
                  </Typography>
                  <RadioGroup
                    className={classes.radioGroup}
                    value={values.updateMinimumRequiredVersion ? 'true' : 'false'}
                    onChange={event => setFieldValue('updateMinimumRequiredVersion', event.target.value === 'true')}
                  >
                    <FormControlLabel
                      classes={{ root: classes.radioRoot }}
                      value="true"
                      control={
                        <Radio size="small" classes={{ root: classes.radioControl, checked: classes.radioChecked }} />
                      }
                      label={
                        <span className={classes.optionLabel}>
                          <span className={classes.optionChoice}>Yes</span>{' '}
                          <span className={classes.optionDescription}>
                            All volunteers must complete this new version.
                          </span>
                        </span>
                      }
                    />
                    <FormControlLabel
                      classes={{ root: classes.radioRoot }}
                      value="false"
                      control={
                        <Radio size="small" classes={{ root: classes.radioControl, checked: classes.radioChecked }} />
                      }
                      label={
                        <span className={classes.optionLabel}>
                          <span className={classes.optionChoice}>No</span>{' '}
                          <span className={classes.optionDescription}>
                            Volunteers who have already completed the minimum required version will not need to fill out
                            this new version.
                          </span>
                        </span>
                      }
                    />
                  </RadioGroup>
                </>
              )}
            </DialogContent>
            <DialogActions className={classes.actions}>
              <Button className={classes.cancelButton} autoFocus onClick={close}>
                Cancel
              </Button>
              <Button className={classes.button} variant="contained" color="primary" type="submit">
                Create New Version
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default ConfirmQuestionnaireVersionDialog
